import { flightsFareClassesClick, flightsFareTypesClick } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import type { FlightDropdownOption } from 'components/Flights/types'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import { FlightsFareTypes, type FlightsClassTypes } from 'constants/flight'
import { useCallback, useRef, useState } from 'react'
import FareDropdownOption from './FareDropdownOption'

interface Props extends Utils.PropsWithDataAttributes {
  options: Array<FlightDropdownOption>
  selectedOption?: FlightDropdownOption
  className?: string
  onOptionChange: (option: FlightDropdownOption<FlightsClassTypes>) => void
}

function FareDropdown(props: Props) {
  const { options, selectedOption, onOptionChange, ...rest } = props
  const [isOpen, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const handleClose = () => setOpen(false)

  const handleOpen = () => {
    setOpen(!isOpen)

    const isFareTypeOptions = options.some(o => o.value === FlightsFareTypes.RETURN)
    if (isFareTypeOptions) {
      fireInteractionEvent(flightsFareTypesClick())
    } else {
      fireInteractionEvent(flightsFareClassesClick())
    }
  }

  const onOptionSelect = useCallback((option: FlightDropdownOption<FlightsClassTypes>) => {
    onOptionChange(option)
    handleClose()
  }, [onOptionChange])

  return (<>
    <TextButton
      {...rest}
      kind="tertiary"
      startIcon={<LineSeatIcon />}
      endIcon={<DropdownChevron open={isOpen} />}
      onClick={handleOpen}
      ref={triggerRef}
    >
      {selectedOption?.label}
    </TextButton>

    <DropdownList
      size="S"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      open={isOpen}
      onClose={handleClose}
      data-testid="fare-class-dropdown-list"
    >
      {options.map(option => (
        <FareDropdownOption
          key={option.value}
          selected={option === selectedOption}
          option={option}
          onOptionSelect={onOptionSelect}
          data-testid={option.value}
        />
      ))}
    </DropdownList>
  </>)
}

export default FareDropdown
